import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ConversationMessage, SourceTrace } from "~/api/conversationTypes";

/**
 * The sources behind ONE answer (W8 §B.6 / S7).
 *
 * There is no link from a message to its traces on the wire. The join is `metadata.streamId`:
 * every gather step a thinker ran while producing a streamed answer carries the same stream
 * id the answer does. That is a weak key — a message with no `streamId` (an older row, a
 * pasted one) simply has no sources, which is the honest answer and not an error.
 *
 * Renders NOTHING when there is nothing to show. A disclosure that opens onto "no sources" is
 * worse than no disclosure: it implies the question was asked and answered.
 */
export function MessageSources({
  message,
  traces,
}: {
  message: ConversationMessage;
  traces: SourceTrace[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const streamId = message.metadata.kind === "ai" ? message.metadata.streamId : null;

  const { sources, engines } = useMemo(() => {
    if (!streamId) {
      return { sources: [], engines: [] as string[] };
    }
    const mine = traces.filter((trace) => trace.streamId === streamId);
    const byUrl = new Map<string, { url: string; title: string | null }>();
    const engineSet = new Set<string>();
    for (const trace of mine) {
      if (trace.engine) {
        engineSet.add(trace.engine);
      }
      for (const source of trace.sources) {
        if (source.url && !byUrl.has(source.url)) {
          byUrl.set(source.url, { url: source.url, title: source.title });
        }
      }
    }
    return { sources: [...byUrl.values()], engines: [...engineSet] };
  }, [streamId, traces]);

  if (sources.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 self-start text-caption text-text-tertiary hover:text-text"
      >
        <span aria-hidden className={open ? "rotate-90" : "rtl:rotate-180"}>
          ▸
        </span>
        {t("{{count}} sources", { count: sources.length })}
        {engines.length > 0 ? ` · ${engines.join(", ")}` : ""}
      </button>
      {open ? (
        <ul className="flex flex-col gap-0.5 ps-4">
          {sources.map((source) => (
            <li key={source.url} className="min-w-0 truncate">
              <a
                href={source.url}
                target="_blank"
                rel="noreferrer noopener"
                dir="ltr"
                className="text-small text-link hover:underline"
              >
                {source.title || source.url}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
